import React, { useState, useRef } from 'react';
import {Button, Dialog} from '@betareader/design-system';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { authUtil } from '../../../utils';
import AppIcon from '../../../components/AppIcon';
// import { useHostAppProxy } from '../../../contexts/HostAppProxyContext';

interface ProfilePhotoUploadProps {
  currentPhotoUrl?: string;
  onPhotoUpdate: (photoUrl: string) => void;
  size?: number;
}

const ProfilePhotoUpload: React.FC<ProfilePhotoUploadProps> = ({
  currentPhotoUrl,
  onPhotoUpdate,
  size = 150,
}) => {
  const { t } = useTranslation();
  const [uploading, setUploading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);


  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.match(/^image\/(jpeg|jpg|png)$/)) {
      toast.error('Please select a valid image file (JPEG, JPG, or PNG)');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    setSelectedFile(file);

    // Create preview URL
    const reader = new FileReader();
    reader.onload = e => {
      setPreviewUrl(e.target?.result as string);
    };
    reader.readAsDataURL(file);

    setShowModal(true);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setUploading(true);
    try {
      const idToken = await authUtil.getFreshIdToken();
      const formData = new FormData();
      formData.append('photo', selectedFile);

      const response = await fetch(
        `${process.env.REACT_APP_API_HOST}/api/v2/profile/photo`,
        {
          method: 'POST',
          headers: {
            'br-token': idToken,
          },
          body: formData,
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.msg || 'Failed to upload photo');
      }

      const result = await response.json();
      onPhotoUpdate(result.photoUrl);
      toast.success('Profile photo updated successfully!');
      setShowModal(false);
      setSelectedFile(null);
      setPreviewUrl(null);
    } catch (error: any) {
      console.error('Photo upload error:', error);
      toast.error(error.message || 'Failed to upload photo');
    } finally {
      setUploading(false);
    }
  };

  const handleCancel = () => {
    setShowModal(false);
    setSelectedFile(null);
    setPreviewUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const triggerFileSelect = () => {
    fileInputRef.current?.click();
  };

  return (
    <>
      <Button variant='ghost' type='button' aria-label={t('UpdateProfilePhoto')}
        onClick={triggerFileSelect}
        style={{position: 'relative', width: size, height: size, borderRadius: '50%',
          padding: 0, overflow: 'hidden', background: 'var(--br-raised-surface)',
          border: '2px dashed var(--br-border)'}}>
        {currentPhotoUrl ? (
          <img
            src={currentPhotoUrl}
            alt=''
            style={{
              width: size,
              height: size,
              objectFit: 'cover',
            }}
          />
        ) : (
          <span
            style={{
              width: size,
              height: size,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <AppIcon name='camera' aria-hidden='true' />
          </span>
        )}
        <span aria-hidden='true' style={{position: 'absolute', right: 8, bottom: 8,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          width: 28, height: 28, borderRadius: '50%',
          background: 'var(--br-accent)', color: 'var(--br-on-accent)'}}>
          <AppIcon name='camera' />
        </span>
      </Button>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />

      <Dialog open={showModal} onClose={handleCancel}
        title={t('UpdateProfilePhoto') || 'Update Profile Photo'}
        actions={<>
          <Button variant='secondary' onClick={handleCancel}>{t('Cancel') || 'Cancel'}</Button>
          <Button onClick={handleUpload} disabled={!selectedFile || uploading}
            aria-busy={uploading}>
            <AppIcon name='upload' aria-hidden='true' />
            {uploading ? 'Uploading...' : t('Upload') || 'Upload'}
          </Button>
        </>}>
          {previewUrl && (
            <div style={{textAlign: 'center'}}>
              <img
                src={previewUrl}
                alt={t('UpdateProfilePhoto')}
                style={{
                  maxWidth: '200px',
                  maxHeight: '200px',
                  objectFit: 'cover',
                  borderRadius: '50%',
                }}
              />
            </div>
          )}
          <p>
            Are you sure you want to upload this photo as your profile picture?
          </p>
          <p>
            <strong>Note:</strong> The image will be publicly visible to other
            users.
          </p>
      </Dialog>
    </>
  );
};

export default ProfilePhotoUpload;
